import { useState } from "react";
import type { WorkspaceMember } from "../../hooks";
import { RoleBadge } from "./RoleBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import { auth } from "../../lib/firebase";
import { getAuthHeader } from "../../lib/auth";

interface MemberCardProps {
  member: WorkspaceMember;
  workspaceId: string;
  onRefetch: () => void;
  currentUserIsLead: boolean;
}

export function MemberCard({ member, workspaceId, onRefetch, currentUserIsLead }: MemberCardProps) {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showMakeLeadConfirm, setShowMakeLeadConfirm] = useState(false);
  const [showRevokeKeyConfirm, setShowRevokeKeyConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [makingLead, setMakingLead] = useState(false);
  const [revokingKey, setRevokingKey] = useState(false);
  const [revokeNotice, setRevokeNotice] = useState<string | null>(null);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const response = await fetch("/api/workspace/members/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
        body: JSON.stringify({
          workspaceId,
          userId: member.user_id,
          removedBy: auth.currentUser?.uid || "unknown",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Remove failed");
      }

      // Refetch members to update UI - await to ensure data is refreshed before hiding loading state
      await onRefetch();
      setShowRemoveConfirm(false);
    } catch (err) {
      console.error("Failed to remove member:", err);
      alert(err instanceof Error ? err.message : "Failed to remove member");
      setRemoving(false);
    }
  };

  const handleMakeLead = async () => {
    setMakingLead(true);
    try {
      const response = await fetch("/api/workspace/members/make-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
        body: JSON.stringify({
          workspaceId,
          newLeadUserId: member.user_id,
          currentUserId: auth.currentUser?.uid || "unknown",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Make Lead failed");
      }

      // Refetch members to update UI - await to ensure data is refreshed before hiding loading state
      await onRefetch();
      setShowMakeLeadConfirm(false);
      setMakingLead(false);
    } catch (err) {
      console.error("Failed to make lead:", err);
      alert(err instanceof Error ? err.message : "Failed to make lead");
      setMakingLead(false);
    }
  };

  // track:workspace-key-onboarding (decision #48248 outcome 4) — "Revoke
  // daemon key," Lead-only, any member including self. Server-enforced
  // (POST /api/workspace/member-key/revoke, human_lead required) —
  // currentUserIsLead's own placeholder-ness above is a pre-existing gap
  // (track:workspace-cleanup, unrelated to this track) this button
  // inherits, not introduces: a non-Lead sees it client-side the same way
  // they already see "Make Lead," and gets the same server 403 either way.
  const handleRevokeKey = async () => {
    setRevokingKey(true);
    try {
      const response = await fetch("/api/workspace/member-key/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getAuthHeader(workspaceId)) },
        body: JSON.stringify({ targetUid: member.user_id, targetEmail: member.email }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Revoke failed");
      }
      const result = await response.json().catch(() => ({}));
      setRevokeNotice(result?.revokedCount > 0 ? "Key revoked" : "No key to revoke");
      setShowRevokeKeyConfirm(false);
    } catch (err) {
      console.error("Failed to revoke key:", err);
      alert(err instanceof Error ? err.message : "Failed to revoke key");
    } finally {
      setRevokingKey(false);
    }
  };

  const isSelf = member.user_id === auth.currentUser?.uid;
  const isLead = member.role === "human_lead";
  const showRemoveButton = !isSelf && !isLead && currentUserIsLead;
  const showMakeLeadButton = !isSelf && !isLead && currentUserIsLead;
  const showRevokeKeyButton = currentUserIsLead;

  return (
    <div className="sc-item-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="sc-item-name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {member.display_name || member.email}
        </div>
        <div className="sc-item-meta">
          Joined {member.joined_at.toLocaleDateString()}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {revokeNotice && (
          <span className="sc-item-meta" role="status">{revokeNotice}</span>
        )}
        <RoleBadge role={member.role} />
        {showMakeLeadButton && (
          <button className="btn sm" onClick={() => setShowMakeLeadConfirm(true)} disabled={makingLead}>
            {makingLead ? "Updating..." : "Make Lead"}
          </button>
        )}
        {showRevokeKeyButton && (
          <button className="btn sm danger" onClick={() => setShowRevokeKeyConfirm(true)} disabled={revokingKey}>
            {revokingKey ? "Revoking..." : "Revoke Key"}
          </button>
        )}
        {showRemoveButton && (
          <button className="btn sm danger" onClick={() => setShowRemoveConfirm(true)} disabled={removing}>
            {removing ? "Removing..." : "Remove"}
          </button>
        )}
      </div>

      {showRemoveConfirm && (
        <ConfirmDialog
          title="Remove Partner"
          message={`Remove ${member.display_name || member.email}? They will lose access to this workspace.`}
          onConfirm={handleRemove}
          onCancel={() => setShowRemoveConfirm(false)}
          confirmLabel="Remove"
        />
      )}

      {showMakeLeadConfirm && (
        <ConfirmDialog
          title="Make Lead"
          message={`Make ${member.display_name || member.email} the new Human Lead? You will become a Partner.`}
          onConfirm={handleMakeLead}
          onCancel={() => setShowMakeLeadConfirm(false)}
          confirmLabel="Make Lead"
        />
      )}

      {showRevokeKeyConfirm && (
        <ConfirmDialog
          title="Revoke Key"
          message={`Revoke ${member.display_name || member.email}'s personal key? It stops working immediately — any computer using it will need a new one.`}
          onConfirm={handleRevokeKey}
          onCancel={() => setShowRevokeKeyConfirm(false)}
          confirmLabel="Revoke"
        />
      )}
    </div>
  );
}
